// src/components/EmailCollectionModal.jsx
// Shows immediately after phone/MSG91 login if user has no email
// Also used in ProfilePage for phone users without email set
import React, { useState } from 'react';
import { auth, db } from '../firebase/firebaseConfig';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { customToast } from '../utils/toastUtils';
import './EmailCollectionModal.css';

const EmailCollectionModal = ({ show, onClose, onEmailUpdated }) => {
  const [email, setEmail] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  if (!show) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !email.trim()) {
      customToast.error('Please enter an email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      customToast.error('Please enter a valid email address');
      return;
    }

    try {
      setIsUpdating(true);
      const user = auth.currentUser;

      if (!user) {
        customToast.error('Session expired. Please login again.');
        onClose();
        return;
      }

      // Save email to Firestore users collection only
      // (Firebase Auth updateEmail requires email verification for phone users - not needed)
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        await updateDoc(userDocRef, {
          email: email.trim().toLowerCase(),
          updatedAt: new Date()
        });
      }

      customToast.success('Email saved successfully!');
      setEmail('');
      if (onEmailUpdated) onEmailUpdated(email.trim().toLowerCase());
      onClose();
    } catch (error) {
      console.error('Error saving email:', error);
      customToast.error('Failed to save email. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="email-collection-overlay" onClick={() => !isUpdating && onClose()}>
      <div className="email-collection-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button 
          className="email-collection-close" 
          onClick={onClose} 
          disabled={isUpdating}
          aria-label="Close"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        <div className="email-collection-header">
          <div className="email-collection-icon">
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2"></rect>
              <polyline points="22,7 12,13 2,7"></polyline>
            </svg>
          </div>
          <h2>Add Your Email</h2>
          <p>We need your email to send payment receipts, plan updates, and important notifications.</p>
        </div>

        <form onSubmit={handleSubmit} className="email-collection-form">
          <div className="email-input-group">
            <label htmlFor="collection-email">Email Address</label>
            <input
              type="email"
              id="collection-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              disabled={isUpdating}
              autoFocus
              required
            />
          </div>

          <div className="email-collection-actions">
            <button
              type="button"
              className="email-skip-btn"
              onClick={onClose}
              disabled={isUpdating}
            >
              Skip for Now
            </button>
            <button
              type="submit"
              className="email-submit-btn"
              disabled={isUpdating || !email.trim()}
            >
              {isUpdating ? 'Saving...' : 'Save Email'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmailCollectionModal;
